import { createAction, createReducer, Reducer } from '@reduxjs/toolkit';

export type TwoWayValue = [bidPrice: string, askPrice: string];
export type NullableTwoWay = [bidPrice: string | null, askPrice: string | null];

export interface QuoteBuilderState {
  account: string | undefined;
  quoteTimer: string | null;
  quantity: string;
  price: TwoWayValue;
  legsPrice: readonly NullableTwoWay[];
}

export const emptyTwoWay: TwoWayValue = ['', ''];

export const INITIAL_STATE: QuoteBuilderState = {
  account: undefined,
  quoteTimer: null,
  quantity: '',
  price: ['', ''],
  legsPrice: [],
};

export interface ChangeLegPriceArgs {
  readonly value: string | null;
  readonly index: number;
  readonly side: 'BUY' | 'SELL';
}

export const changeAccount = createAction<string>('changeAccount');
export const changeQuoteTimer = createAction<string>('changeQuoteTimer');
export const changeQuantity = createAction<string>('changeQuantity');
export const changePrice = createAction<NullableTwoWay>('changePrice');
export const clearPrice = createAction('clearPrice');
export const changeInputLegPrice = createAction<TwoWayValue[]>(
  'changeInputLegPrice',
);
export const resetLegPrices = createAction('resetLegPrices');
export const changeLegPrice =
  createAction<ChangeLegPriceArgs>('changeLegPrice');
export const switchLegPrices = createAction('switchLegPrices');

const reducer: Reducer<QuoteBuilderState> = createReducer(
  INITIAL_STATE,
  (builder) =>
    builder
      .addCase(changeAccount, (state, action) => {
        state.account = action.payload;
      })
      .addCase(changeQuoteTimer, (state, action) => {
        state.quoteTimer = action.payload;
      })
      .addCase(changeQuantity, (state, action) => {
        state.quantity = action.payload;
      })
      .addCase(changePrice, (state, action) => {
        const twoWayPrice = action.payload;
        state.price[0] = twoWayPrice[0] ?? state.price[0];
        state.price[1] = twoWayPrice[1] ?? state.price[1];
      })
      .addCase(clearPrice, (state) => {
        state.price = ['', ''];
      })
      .addCase(changeInputLegPrice, (state, { payload }) => {
        state.legsPrice = payload;
      })
      .addCase(resetLegPrices, (state) => {
        state.legsPrice = state.legsPrice.map(() => ['', '']);
      })
      .addCase(switchLegPrices, (state) => {
        state.legsPrice = state.legsPrice.map(
          ([bidPrice, askPrice]) => [askPrice, bidPrice] as TwoWayValue,
        );
      })
      .addCase(changeLegPrice, (state, { payload }) => {
        state.legsPrice = state.legsPrice.map((prices, index) => {
          if (payload.index !== index) return prices;

          const [bidPrice, askPrice] = prices;
          return payload.side === 'BUY'
            ? [payload.value, askPrice]
            : [bidPrice, payload.value];
        });
      }),
);

export default reducer;

export type QuoteBuilderReducer = typeof reducer;
